import React, { useCallback, useRef, useState, memo, useEffect } from "react";
import axios from "axios";

function useCenteredDrag() {
    const [offset, setOffset] = useState({ x: 0, y: 0 });
    const startRef = useRef({ x: 0, y: 0, ox: 0, oy: 0 });

    const onDragStart = useCallback(
        (e) => {
            const startX = "clientX" in e ? e.clientX : e.touches?.[0]?.clientX;
            const startY = "clientY" in e ? e.clientY : e.touches?.[0]?.clientY;

            startRef.current = { x: startX, y: startY, ox: offset.x, oy: offset.y };

            const onMove = (ev) => {
                const cx = "clientX" in ev ? ev.clientX : ev.touches?.[0]?.clientX;
                const cy = "clientY" in ev ? ev.clientY : ev.touches?.[0]?.clientY;
                const dx = cx - startRef.current.x;
                const dy = cy - startRef.current.y;
                setOffset({ x: startRef.current.ox + dx, y: startRef.current.oy + dy });
            };

            const onUp = () => {
                document.removeEventListener("mousemove", onMove);
                document.removeEventListener("mouseup", onUp);
                document.removeEventListener("touchmove", onMove);
                document.removeEventListener("touchend", onUp);
            };

            document.addEventListener("mousemove", onMove);
            document.addEventListener("mouseup", onUp);
            document.addEventListener("touchmove", onMove, { passive: false });
            document.addEventListener("touchend", onUp);
        },
        [offset.x, offset.y]
    );

    return { offset, onDragStart };
}

export const DraggableModalBox = memo(function DraggableModalBox({ onClose, onFocus, children }) {
    const { offset, onDragStart } = useCenteredDrag();
    const boxRef = useRef(null);

    // Helper function to create/show the yellow popup
    const showYellowPopup = (e, term, definition) => {
        const existing = document.getElementById("glossary-popup");
        if (existing) existing.remove();

        const popup = document.createElement("div");
        popup.id = "glossary-popup";
        Object.assign(popup.style, {
            position: 'absolute',
            top: `${e.pageY + 10}px`,
            left: `${e.pageX}px`,
            backgroundColor: '#fffbe6', // Match yellow style
            border: '1px solid #000',
            padding: '8px',
            zIndex: '10000',
            maxWidth: '300px',
            boxShadow: '2px 2px 5px rgba(0,0,0,0.2)',
            fontSize: '13px',
            fontFamily: 'sans-serif',
            pointerEvents: 'none' // Clicks pass through it
        });

        popup.innerHTML = `<strong>${term}</strong><hr style="margin:4px 0"/>${definition}`;
        document.body.appendChild(popup);

        const removePopup = () => {
            if (popup) popup.remove();
            window.removeEventListener("scroll", removePopup, true);
            document.removeEventListener("click", removePopup);
        };

        // Disappear on scroll (capture phase for inner divs) or next click
        window.addEventListener("scroll", removePopup, true);
        setTimeout(() => document.addEventListener("click", removePopup), 10);
    };
    const isResizeCorner = (event) => {
        const box = boxRef.current;
        if (!box) return false;
        const rect = box.getBoundingClientRect();
        const pointerX = "clientX" in event ? event.clientX : event.touches?.[0]?.clientX;
        const pointerY = "clientY" in event ? event.clientY : event.touches?.[0]?.clientY;
        if (pointerX == null || pointerY == null) return false;
        const resizeGrip = 24;
        return (
            pointerX >= rect.right - resizeGrip &&
            pointerY >= rect.bottom - resizeGrip
        );
    };

    const handleMouseDown = (e) => {
        // Ignore if clicking on interactive elements
        if (e.target.closest("button, a, input, textarea, select")) return;
        // Let native CSS resize handle take over.
        if (isResizeCorner(e)) return;

        // Ignore if user is selecting text
        if (window.getSelection && window.getSelection().toString().length > 0) return;

        // Only left mouse button
        if (e.button !== 0) return;

        onDragStart(e);
    };

    const handleTouchStart = (e) => {
        // Ignore if clicking on interactive elements
        if (e.target.closest("button, a, input, textarea, select")) return;
        // Do not start drag from resize grip area.
        if (isResizeCorner(e)) return;

        // Ignore if user is selecting text
        if (window.getSelection && window.getSelection().toString().length > 0) return;

        onDragStart(e);
    };

    return (
        <div
            ref={boxRef}
            role="dialog"
            aria-modal="true"
            className="fixed bg-white rounded-xl shadow-lg h-[75vh] w-[75vh] resize overflow-auto p-6 cursor-grab active:cursor-grabbing"
            style={{
                top: "50%",
                left: "50%",
                transform: `translate(calc(-50% + ${offset.x}px), calc(-50% + ${offset.y}px))`,
                zIndex: 1000,
            }}
            onClick={(e) => {
                e.stopPropagation();
                onFocus?.();
            }}
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
        >
            {children}
        </div>
    );
});
